import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';

const PX = 1.1; // pixels per minute
const STATUS = {
  pending: { label: 'En attente', cls: 'bg-amber-100 border-amber-400 text-amber-900' },
  confirmed: { label: 'Confirmée', cls: 'bg-blue-100 border-blue-500 text-blue-900' },
  completed: { label: 'Terminée', cls: 'bg-emerald-100 border-emerald-500 text-emerald-900' },
  cancelled: { label: 'Annulée', cls: 'bg-slate-100 border-slate-300 text-slate-500 line-through opacity-70' },
};
const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const monday = (d) => addDays(d, -((d.getDay() + 6) % 7));
const dayLabel = (s) => new Date(`${s}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' });

const Block = ({ q, dayStart, labels, onOpen, split }) => {
  const r = q.reservation;
  const st = STATUS[q.status] || STATUS.pending;
  const cancelled = q.status === 'cancelled';
  const pos = !split ? 'left-1 right-1 z-10' : cancelled ? 'left-[66%] right-1 z-0' : 'left-1 right-[35%] z-10';
  return (
    <>
    <button type="button" onClick={() => onOpen(q)} data-testid={`planning-item-${q.id}`}
      className={`absolute ${pos} rounded-lg border-l-4 px-2 py-1 text-left text-xs overflow-hidden shadow-sm hover:shadow transition-shadow ${st.cls}`}
      style={{ top: (r.start_minute - dayStart) * PX, height: Math.max(r.duration_minutes * PX, 34) }}>
      <p className="font-bold truncate">{hhmm(r.start_minute)}–{hhmm(r.start_minute + r.duration_minutes)} · {q.first_name} {q.last_name}</p>
      <p className="truncate">{q.services.map((s) => labels[s] || s).join(', ')}</p>
      <p className="truncate font-semibold" data-testid={`planning-status-${q.id}`}>{st.label}</p>
    </button>
    {r.buffer_minutes > 0 && q.status !== 'cancelled' && (
      <div className={`absolute ${split ? 'left-1 right-[35%]' : 'left-1 right-1'} rounded-b-lg bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(100,116,139,.18)_4px,rgba(100,116,139,.18)_8px)]`}
        style={{ top: (r.start_minute + r.duration_minutes - dayStart) * PX, height: r.buffer_minutes * PX }} title="Temps tampon" data-testid={`planning-buffer-${q.id}`} />
    )}
    </>
  );
};

const DayColumn = ({ day, range, labels, onOpen }) => {
  const closed = !day.open || day.holiday;
  return (
    <div className="min-w-[150px] flex-1" data-testid={`planning-day-${day.date}`}>
      <p className="text-sm font-semibold capitalize text-slate-800 h-10 flex items-center justify-center text-center">{dayLabel(day.date)}</p>
      <div className="relative border-l border-slate-200 bg-white" style={{ height: (range[1] - range[0]) * PX }}>
        {!closed && (
          <div className="absolute inset-x-0 bg-blue-50/40" style={{ top: (toMin(day.start) - range[0]) * PX, height: (toMin(day.end) - toMin(day.start)) * PX }} />
        )}
        {closed && <p className="absolute inset-x-0 top-4 text-center text-xs text-slate-400">{day.holiday ? 'Jour férié' : 'Fermé'}</p>}
        {day.unavailabilities.map((u) => {
          const a = u.start_time ? toMin(u.start_time) : range[0];
          const b = u.end_time ? toMin(u.end_time) : range[1];
          return (
            <div key={u.id} className="absolute inset-x-0 bg-slate-200/70 text-[10px] text-slate-600 px-1" style={{ top: (a - range[0]) * PX, height: (b - a) * PX }}>
              Indisponible{u.reason && ` · ${u.reason}`}
            </div>
          );
        })}
        {day.reservations.map((q) => <Block key={q.id} q={q} dayStart={range[0]} labels={labels} onOpen={onOpen}
          split={day.reservations.some((x) => x.status === 'cancelled') && day.reservations.some((x) => x.status !== 'cancelled')} />)}
      </div>
    </div>
  );
};

const PlanningAdmin = ({ labels, onOpenQuote, refreshKey }) => {
  const [view, setView] = useState('week');
  const [anchor, setAnchor] = useState(() => new Date());
  const [data, setData] = useState(null);
  const start = view === 'week' ? monday(anchor) : anchor;
  const days = view === 'week' ? 7 : 1;
  const startIso = iso(start);

  useEffect(() => {
    api.get('/admin/planning', { params: { start: startIso, days } })
      .then((r) => setData(r.data)).catch(() => toast.error('Erreur de chargement du planning'));
  }, [startIso, days, refreshKey]);

  const all = data?.days || [];
  const mins = all.filter((d) => d.open).flatMap((d) => [toMin(d.start), toMin(d.end)]);
  all.forEach((d) => d.reservations.forEach((q) => mins.push(q.reservation.start_minute, q.reservation.start_minute + q.reservation.duration_minutes + q.reservation.buffer_minutes)));
  const range = [Math.floor(Math.min(...(mins.length ? mins : [600])) / 60) * 60, Math.ceil(Math.max(...(mins.length ? mins : [1020])) / 60) * 60];
  const hours = [];
  for (let m = range[0]; m <= range[1]; m += 60) hours.push(m);
  const count = all.reduce((n, d) => n + d.reservations.filter((q) => q.status !== 'cancelled').length, 0);

  return (
    <div className="space-y-4" data-testid="planning-admin">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 bg-white overflow-hidden">
          {[['week', 'Semaine'], ['day', 'Jour']].map(([v, t]) => (
            <button key={v} type="button" onClick={() => setView(v)} data-testid={`planning-view-${v}`}
              className={`px-3 py-2 text-sm font-semibold ${view === v ? 'bg-[hsl(207,90%,54%)] text-white' : 'text-slate-600'}`}>{t}</button>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={() => setAnchor(addDays(anchor, -days))} data-testid="planning-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" onClick={() => setAnchor(new Date())} data-testid="planning-today">Aujourd'hui</Button>
        <Button size="sm" variant="outline" onClick={() => setAnchor(addDays(anchor, days))} data-testid="planning-next"><ChevronRight className="w-4 h-4" /></Button>
        <input type="date" value={iso(anchor)} onChange={(e) => e.target.value && setAnchor(new Date(`${e.target.value}T12:00:00`))}
          className="h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="planning-date-input" />
        <span className="text-sm text-slate-500 flex items-center gap-1 ml-auto" data-testid="planning-count"><CalendarDays className="w-4 h-4" />{count} réservation(s) active(s)</span>
      </div>
      <div className="flex flex-wrap gap-3 text-xs" data-testid="planning-legend">
        {Object.entries(STATUS).map(([k, v]) => <span key={k} className={`px-2 py-0.5 rounded border-l-4 ${v.cls}`}>{v.label}</span>)}
        <span className="px-2 py-0.5 rounded bg-slate-200/70 text-slate-600">Indisponible</span>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-2 overflow-x-auto">
        <div className="flex">
          <div className="w-12 shrink-0">
            <div className="h-10" />
            <div className="relative" style={{ height: (range[1] - range[0]) * PX }}>
              {hours.map((m) => <span key={m} className="absolute right-1 -translate-y-1/2 text-[10px] text-slate-400" style={{ top: (m - range[0]) * PX }}>{hhmm(m)}</span>)}
            </div>
          </div>
          {all.map((d) => <DayColumn key={d.date} day={d} range={range} labels={labels} onOpen={onOpenQuote} />)}
        </div>
      </div>
    </div>
  );
};

export default PlanningAdmin;
